import { useEffect, useMemo, useState } from 'react';

import { SelectOption } from '@/components/ui/SelectWithNewOption/SelectWithNewOption';
import useToast, { TOAST_LABELS } from '@/hooks/useToast';
import { useCreateAgentConfigMutation, useGetAgentConfigsQuery, useUpdateAgentConfigMutation } from '@/store/rest/configApi';
import { useCreateCredentialMutation, useLazyGetCredentialsByTypeQuery } from '@/store/rest/credentialApi';
import { TAgent } from '@/types/agents';
import { TAgentConfig, TAgentConfigCreate, TAgentConfigUpdate } from '@/types/configs';
import { TCredential, TCredentialCreate, TSystemCredential } from '@/types/credentials';
import { resolveSlotCredentialReferenceIds } from '@/utils/mapCredentialsToSchemaSlots';

const WARNING_MESSAGE = `The previous config state is invalid — some credentials were removed. 
Please select valid credentials and save the config before continuing.`;

export enum PendingActionType {
  Continue = 'continue',
  SwitchConfig = 'switchConfig',
  Leave = 'leave',
}

type TCreateConfigData = {
  configName: string;
  selectedCredentials: Record<string, string>;
};

interface UseConfigureStageProps {
  agent: TAgent;
  configId: string;
  selectedCredentials: Record<string, string>;
  setSelectedCredentials: (credentials: Record<string, string>) => void;
  onConfigNameChange: (value: string) => void;
  onContinue: () => void;
}

const useConfigureStage = ({
  agent,
  configId,
  selectedCredentials,
  setSelectedCredentials,
  onConfigNameChange,
  onContinue,
}: UseConfigureStageProps) => {
  const { showSuccess, showWarning, fromException } = useToast();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] = useState(false);
  const [pendingActionType, setPendingActionType] = useState<PendingActionType | null>(null);
  const [pendingConfigId, setPendingConfigId] = useState<string | null>(null);
  const [pendingLeaveAction, setPendingLeaveAction] = useState<(() => void) | null>(null);
  const [selectedCredential, setSelectedCredential] = useState<TSystemCredential | null>(null);
  const [credentialOptions, setCredentialOptions] = useState<Record<string, SelectOption[]>>({});
  const [configOptions, setConfigOptions] = useState<SelectOption[]>([]);

  const { data: agentConfigs, refetch: refetchAgentConfigs } = useGetAgentConfigsQuery({ agentId: agent.referenceId });
  const [getCredentialsByType] = useLazyGetCredentialsByTypeQuery();
  const [createCredential, { isLoading: isCreatingCredential }] = useCreateCredentialMutation();
  const [createAgentConfig, { isLoading: isCreatingAgentConfig }] = useCreateAgentConfigMutation();
  const [updateAgentConfig, { isLoading: isUpdatingAgentConfig }] = useUpdateAgentConfigMutation();

  const handleAddNewCredential = (credential: TSystemCredential) => {
    setSelectedCredential(credential);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCredential(null);
  };

  const handleSubmitCredential = async (data: TCredentialCreate) => {
    if (!selectedCredential) {
      return;
    }

    try {
      const response = await createCredential(data).unwrap();

      const newOption: SelectOption = {
        value: response.referenceId,
        label: response.customName,
      };
      setCredentialOptions((prev) => ({
        ...prev,
        [response.name]: [...(prev[response.name] || []), newOption],
      }));

      handleCloseModal();

      showSuccess({ label: TOAST_LABELS.createSuccess, message: 'Credential created successfully' });
    } catch (error) {
      fromException({ error, label: TOAST_LABELS.createError });
    }
  };

  const handleCreateCredentialForConfig = async (
    _credential: TSystemCredential,
    data: TCredentialCreate
  ): Promise<{ referenceId: string; customName: string; name: string }> => {
    try {
      const response = await createCredential(data).unwrap();
      const newOption: SelectOption = {
        value: response.referenceId,
        label: response.customName,
      };
      setCredentialOptions((prev) => ({
        ...prev,
        [response.name]: [...(prev[response.name] || []), newOption],
      }));
      showSuccess({ label: TOAST_LABELS.createSuccess, message: 'Credential created successfully' });

      return { referenceId: response.referenceId, customName: response.customName, name: response.name };
    } catch (error) {
      fromException({ error, label: TOAST_LABELS.createError });

      throw error;
    }
  };

  const getCredentialOptions = async (credentialType: string, credentialName: string): Promise<SelectOption[]> => {
    try {
      const credentials = await getCredentialsByType({ type: credentialType, name: credentialName }).unwrap();

      const options = credentials.map((credential: TCredential) => ({
        value: credential.referenceId,
        label: credential.customName,
      }));

      return options;
    } catch (error) {
      fromException({ error, label: TOAST_LABELS.error });

      return [];
    }
  };

  const handleCreateConfig = async (data: TCreateConfigData) => {
    const credentialIds = agent.credentials.map(
      (_, slotIndex) => data.selectedCredentials[`${agent.referenceId}:${slotIndex}`] ?? ''
    );
    const hasEmptyCredentialSlot = credentialIds.some((id) => !id);
    if (hasEmptyCredentialSlot) {
      return;
    }

    const newConfig: TAgentConfigCreate = {
      agentId: agent.referenceId,
      name: data.configName,
      credentialIds,
    };
    try {
      const response = await createAgentConfig(newConfig).unwrap();
      setConfigOptions((prev) => [...prev, { value: response.referenceId, label: response.name }]);
      onConfigNameChange(response.referenceId);
      setSelectedCredentials(data.selectedCredentials);
      showSuccess({ label: TOAST_LABELS.createSuccess, message: 'Config created successfully' });
      refetchAgentConfigs();
    } catch (error) {
      fromException({ error, label: TOAST_LABELS.createError });
    }
    setIsConfigModalOpen(false);
  };

  const handleUpdateConfig = async () => {
    if (!configId) {
      return;
    }

    const credentialIds = agent.credentials.map(
      (_, slotIndex) => selectedCredentials[`${agent.referenceId}:${slotIndex}`] ?? ''
    );
    const hasEmptyCredentialSlot = credentialIds.some((id) => !id);
    if (hasEmptyCredentialSlot) {
      return;
    }

    const updatedConfig: TAgentConfigUpdate = {
      referenceId: configId,
      credentialIds,
    };
    try {
      await updateAgentConfig(updatedConfig).unwrap();
      showSuccess({ label: TOAST_LABELS.updateSuccess, message: 'Config updated successfully' });
      refetchAgentConfigs();
    } catch (error) {
      fromException({ error, label: TOAST_LABELS.updateError });
    }
  };

  const handleContinue = () => {
    if (isConfigEdited) {
      setPendingActionType(PendingActionType.Continue);
      setIsUnsavedChangesModalOpen(true);
    } else {
      onContinue();
    }
  };

  const handleConfigChange = (newConfigId: string) => {
    if (newConfigId === configId) {
      return;
    }
    if (isConfigEdited) {
      setPendingActionType(PendingActionType.SwitchConfig);
      setPendingConfigId(newConfigId);
      setIsUnsavedChangesModalOpen(true);
    } else {
      onConfigNameChange(newConfigId);
    }
  };

  const requestLeave = (action: () => void) => {
    if (isConfigEdited) {
      setPendingActionType(PendingActionType.Leave);
      setPendingLeaveAction(() => action);
      setIsUnsavedChangesModalOpen(true);
    } else {
      action();
    }
  };

  const handleDiscardChanges = () => {
    const actionType = pendingActionType;
    const configIdToSwitch = pendingConfigId;
    const leaveAction = pendingLeaveAction;
    setIsUnsavedChangesModalOpen(false);
    setPendingActionType(null);
    setPendingConfigId(null);
    setPendingLeaveAction(null);

    if (actionType === PendingActionType.SwitchConfig && configIdToSwitch) {
      onConfigNameChange(configIdToSwitch);

      return;
    }

    if (actionType === PendingActionType.Leave && leaveAction) {
      leaveAction();

      return;
    }

    if (actionType === PendingActionType.Continue && configId && agentConfigs) {
      const selectedConfig = agentConfigs.find((config: TAgentConfig) => config.referenceId === configId);

      if (!selectedConfig) {
        return;
      }

      const schemaSlots = agent.credentials.map((c) => ({ type: c.type, name: c.name }));
      const revertedReferenceIds = resolveSlotCredentialReferenceIds(
        schemaSlots,
        selectedConfig.credentials,
        selectedConfig.credentialIds
      );
      const revertedCredentials: Record<string, string> = {};
      revertedReferenceIds.forEach((referenceId, slotIndex) => {
        const slotKey = `${agent.referenceId}:${slotIndex}`;
        if (referenceId) {
          revertedCredentials[slotKey] = referenceId;
        }
      });
      setSelectedCredentials(revertedCredentials);

      const allRequiredPresent = agent.credentials.every((_, slotIndex) => {
        const slotKey = `${agent.referenceId}:${slotIndex}`;

        return Boolean(revertedCredentials[slotKey]);
      });
      const allSelectedStillExist = agent.credentials.every((cred, slotIndex) => {
        const slotKey = `${agent.referenceId}:${slotIndex}`;
        const selectedId = revertedCredentials[slotKey];
        const hasSelectedId = Boolean(selectedId);
        if (!hasSelectedId) {
          return false;
        }

        const options = credentialOptions[cred.name] || [];
        const isSelectedIdInOptions = options.some((opt) => opt.value === selectedId);

        return isSelectedIdInOptions;
      });

      const canContinueAfterRevert = allRequiredPresent && allSelectedStillExist;
      if (canContinueAfterRevert) {
        onContinue();
      } else {
        showWarning(WARNING_MESSAGE, { autoClose: 10000 });
      }
    }
  };

  const handleStayAndContinueEditing = () => {
    setIsUnsavedChangesModalOpen(false);
    setPendingActionType(null);
    setPendingConfigId(null);
    setPendingLeaveAction(null);
  };

  const initializePreselectedCredentials = () => {
    if (!configId || !agentConfigs) {
      return;
    }
    const selectedConfig = agentConfigs.find((config: TAgentConfig) => config.referenceId === configId);
    if (selectedConfig) {
      const schemaSlots = agent.credentials.map((c) => ({ type: c.type, name: c.name }));
      const orderedReferenceIds = resolveSlotCredentialReferenceIds(
        schemaSlots,
        selectedConfig.credentials,
        selectedConfig.credentialIds
      );
      const preselectedCredentials: Record<string, string> = {};
      orderedReferenceIds.forEach((referenceId, slotIndex) => {
        const slotKey = `${agent.referenceId}:${slotIndex}`;
        if (referenceId) {
          preselectedCredentials[slotKey] = referenceId;
        }
      });
      setSelectedCredentials(preselectedCredentials);
    }
  };

  useEffect(() => {
    agent.credentials.forEach(async (credential) => {
      const options = await getCredentialOptions(credential.type, credential.name);
      setCredentialOptions((prev) => ({ ...prev, [credential.name]: options }));
    });
  }, [agent.credentials]);

  useEffect(() => {
    if (agentConfigs) {
      const mappedConfigOptions = agentConfigs.map((config: TAgentConfig) => ({ value: config.referenceId, label: config.name }));
      setConfigOptions(mappedConfigOptions);
    }
  }, [agentConfigs]);

  useEffect(() => {
    initializePreselectedCredentials();
  }, [configId, agentConfigs]);

  const isConfigEdited = useMemo(() => {
    if (!configId || !agentConfigs) {
      return false;
    }

    const selectedConfig = agentConfigs.find((config: TAgentConfig) => config.referenceId === configId);
    if (!selectedConfig) {
      return false;
    }

    const schemaSlots = agent.credentials.map((c) => ({ type: c.type, name: c.name }));
    const savedReferenceIds = resolveSlotCredentialReferenceIds(
      schemaSlots,
      selectedConfig.credentials,
      selectedConfig.credentialIds
    );

    for (let slotIndex = 0; slotIndex < agent.credentials.length; slotIndex += 1) {
      const slotKey = `${agent.referenceId}:${slotIndex}`;
      const stateId = selectedCredentials[slotKey];
      const savedId = savedReferenceIds[slotIndex];
      const isCredentialSlotEdited = stateId !== savedId;
      if (isCredentialSlotEdited) {
        return true;
      }
    }

    return false;
  }, [configId, agentConfigs, selectedCredentials, agent.credentials, agent.referenceId]);

  return {
    agentConfigs,
    isModalOpen,
    configOptions,
    setIsModalOpen,
    isCreatingCredential,
    isCreatingAgentConfig,
    isUpdatingAgentConfig,
    isConfigModalOpen,
    setIsConfigModalOpen,
    isUnsavedChangesModalOpen,
    setIsUnsavedChangesModalOpen,
    selectedCredential,
    setSelectedCredential,
    credentialOptions,
    isConfigEdited,
    handleAddNewCredential,
    handleCloseModal,
    handleSubmitCredential,
    handleCreateCredentialForConfig,
    getCredentialOptions,
    handleCreateConfig,
    handleUpdateConfig,
    handleContinue,
    handleConfigChange,
    handleDiscardChanges,
    handleStayAndContinueEditing,
    requestLeave,
  };
};

export default useConfigureStage;